import React, { useState, useEffect, useContext, useMemo } from "react";
import { Radio, Space, Button, Dropdown, Tooltip } from "antd";
import SideBarDrawer from "./sideBarDrawer";
import { DownOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { SocketContext } from "../../context/socketContext";
import DrageCurrentChat from "./drageCurrentChat";
import DragPastChat from "./dragPastChat";
import EmailModal from "../emailModal/EmailModal";
import TransferModal from "../transferModal/TransferModal";
import BlockModal from "../blockModal/BlockModal";
import { SOCKET_EVENTS } from "../../constants/socketEvents";
import { messageRowFromAgentJoinedEvent } from "../../helpers/conversationDisplay";
import { computePastChats, mergeVisitorProfile, visitorProfileMatches } from "../../helpers/visitorPastStats";

export default function DrageableBody({
  currentVisitor,
  isDrawerTarget,
  registerDrawerHeaderActions,
  clearDrawerHeaderActions,
  visitorDrawerCompact = true,
  onToggleDrawerWidth,
}) {
  const [messages, setMessages] = useState([]);
  const [isAnimation, setisAnimation] = useState(false);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.Auth.userData);
  const servedData = useSelector((state) => state.VisitorReducer.data);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { Socket } = useContext(SocketContext);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const pastChatDetails = useSelector((state) => state.PastChat.data);
  const usertoken = useSelector((state) => state.Auth.token);
  const [size, setSize] = useState("1");
  const [runAi, setRunAi] = useState(false);

  const onChange = (e) => {
    setSize(e.target.value);
  };

  // The agent counts as "viewing" a chat (so incoming messages are seen, not just
  // delivered) only when they have JOINED it and the drawer is open (not minimized).
  // "Joined" mirrors the same signal the composer uses (servedData / servedBy), with
  // agentParticipants as a fallback — relying on agentParticipants alone is unreliable
  // because it can be stale/unpopulated, which left messages stuck on "delivered".
  const isJoined = useMemo(
    () => {
      const servingInStore = servedData?.some(
        (it) => String(it?.unique_id) === String(currentVisitor?.unique_id)
      );
      const servedByMe = currentVisitor?.servedBy?.some(
        (item) => String(item?.id) === String(userData?._id)
      );
      const inParticipants = currentVisitor?.current_conversation?.agentParticipants?.some(
        (p) => String(p?._id) === String(userData?._id)
      );
      return !!(servingInStore || servedByMe || inParticipants);
    },
    [
      servedData,
      currentVisitor?.servedBy,
      currentVisitor?.current_conversation?.agentParticipants,
      currentVisitor?.unique_id,
      userData?._id,
    ]
  );

  useEffect(() => {
    if (!Socket) return;
    const onJoin = (data) => {
      if (currentVisitor?.unique_id != data?.unique_id) {
        return;
      }
      const row = messageRowFromAgentJoinedEvent(data);
      if (!row) {
        return;
      }
      setisAnimation(false);
      setMessages((prev) => [...prev, row]);
    };
    Socket.on("agentJoinedChat", onJoin);

    const onAgentLeave = (data) => {
      if (currentVisitor?.unique_id == data?.unique_id) {
        setMessages(data?.updatedMessages);
        setisAnimation(false);
      }
    };
    Socket.on("agentLeave", onAgentLeave);

    const handleReceive = (message) => {
      const isFromVisitor =
        message?.sender === "visitor" || String(message?.senderID) === String(currentVisitor?.unique_id);
      const matchesRoom = message?.to && message?.to === currentVisitor?.roomId;
      const matchesVisitor =
        isFromVisitor && String(message?.senderID) === String(currentVisitor?.unique_id);
      if (!matchesRoom && !matchesVisitor) {
        return;
      }

      // Seen only when the agent has joined AND the drawer is open; otherwise delivered.
      const receiptStatus = isJoined && !currentVisitor?.minimized ? "seen" : "delivered";

      setMessages((prev) => {
        const uid = message?.uniqueMessageID;
        if (uid && prev?.some((m) => m?.uniqueMessageID === uid)) {
          // Avoid duplicates: update the existing optimistic row (status/file/audio/time, etc.)
          return prev.map((m) => (m?.uniqueMessageID === uid ? { ...m, ...message } : m));
        }
        return [...prev, { ...message, status: receiptStatus }];
      });

      // Only visitor -> agent messages should trigger read receipts + AI suggestion.
      if (!isFromVisitor) {
        return;
      }
      setRunAi(true);
      setisAnimation(true);
      // Visitor->agent messages don't carry `to`, so the server can't route the
      // receipt back to the widget room without this fallback. Without it the
      // visitor's tick stays "delivered" even when the agent is viewing.
      const out = { ...message, status: receiptStatus, to: message?.to || currentVisitor?.roomId };
      Socket.emit("visitorMsgToServer", out);
    };
    Socket.on(SOCKET_EVENTS.VISITOR_CHAT_TO_BRAND, handleReceive);

    return () => {
      Socket.off("agentJoinedChat", onJoin);
      Socket.off("agentLeave", onAgentLeave);
      Socket.off(SOCKET_EVENTS.VISITOR_CHAT_TO_BRAND, handleReceive);
    };
  }, [Socket, currentVisitor?.unique_id, currentVisitor?.roomId, currentVisitor?.minimized, isJoined]);

  const toggleReply = (toggler) => {
    if (!Socket) return;
    Socket.emit("AiReplyToggler", { unique_id: currentVisitor?.unique_id, toggler: toggler });
  };

  const BlockVisitor = () => {
    setIsModalOpen(true);
  };

  const items = useMemo(
    () =>
      [
        {
          label: (
            <div onClick={() => BlockVisitor()}>
              <span>Block</span>
            </div>
          ),
          key: "0",
        },
        currentVisitor?.current_conversation &&
          currentVisitor?.servedBy?.some((x) => x?.id == userData?._id) && {
            label: (
              <div onClick={() => setTransferModalOpen(true)}>
                <span>Transfer Chat to Agent</span>
              </div>
            ),
            key: "1",
          },
        {
          label: (
            <div onClick={() => toggleReply(!currentVisitor?.autoReply)}>
              <span>Turn Ai Reply : {currentVisitor?.autoReply ? "Off" : "On"} </span>
            </div>
          ),
          key: "3",
        },
      ].filter(Boolean),
    [
      currentVisitor?.unique_id,
      currentVisitor?.autoReply,
      currentVisitor?.current_conversation,
      currentVisitor?.servedBy,
      userData?._id,
    ]
  );

  useEffect(() => {
    if (!registerDrawerHeaderActions || !clearDrawerHeaderActions) return undefined;
    const uid = currentVisitor?.unique_id;
    if (!isDrawerTarget || !uid) return undefined;
    registerDrawerHeaderActions(
      uid,
      <Dropdown menu={{ items }} trigger={["click"]} overlayClassName="Actions_visitors">
        <Button type="default" className="chatVisitorDrawer__actionsBtn" onClick={(e) => e.preventDefault()}>
          <Space>
            Actions
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    );
    return () => clearDrawerHeaderActions(uid);
  }, [isDrawerTarget, registerDrawerHeaderActions, clearDrawerHeaderActions, currentVisitor?.unique_id, items]);

  const pastCount = useMemo(() => {
    const fetched = visitorProfileMatches(pastChatDetails, currentVisitor?.unique_id)
      ? pastChatDetails
      : null;
    const profile = mergeVisitorProfile(currentVisitor, fetched);
    return computePastChats(profile);
  }, [currentVisitor, pastChatDetails]);

  return (
    <>
      <TransferModal
        SelectedVisitorData={currentVisitor}
        setTransferModalOpen={setTransferModalOpen}
        transferModalOpen={transferModalOpen}
      />
      <BlockModal
        userData={userData}
        vistiorData={currentVisitor}
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        Socket={Socket}
      />
      <EmailModal
        userData={userData}
        vistiorData={currentVisitor}
        setIsEmailModalOpen={setIsEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        usertoken={usertoken}
      />
      <div className="chatVisitorDrawer__bodyRoot">
        <div className="chatVisitorDrawer__tabsRow">
          {(() => {
            const isWidgetOpen = currentVisitor?.isWidgetOpen;
            const dotColor =
              isWidgetOpen === true ? "#22c55e" : isWidgetOpen === false ? "#ef4444" : "#9ca3af";
            const dotTitle =
              isWidgetOpen === true
                ? "Widget open"
                : isWidgetOpen === false
                  ? "Widget closed"
                  : "Widget status unknown";
            return (
              <Tooltip placement="bottom" title={dotTitle}>
                <span
                  aria-label={dotTitle}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: dotColor,
                    display: "inline-block",
                    marginRight: 10,
                    flex: "0 0 auto",
                    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
                  }}
                />
              </Tooltip>
            );
          })()}
          <Radio.Group value={size} onChange={onChange} className="chatVisitorDrawer__tabs">
            <Radio.Button value="1">Current chat</Radio.Button>
            <Radio.Button value="2">{`Past chats${pastCount > 0 ? ` (${pastCount})` : " (0)"}`}</Radio.Button>
          </Radio.Group>
          {isDrawerTarget && typeof onToggleDrawerWidth === "function" ? (
            <Tooltip
              title={
                visitorDrawerCompact
                  ? "Expand drawer — full width and visitor profile"
                  : "Compact — narrower drawer and hide visitor profile"
              }
            >
              <button
                type="button"
                className={`chatVisitorDrawer__drawerWidthToggle${visitorDrawerCompact ? " chatVisitorDrawer__drawerWidthToggle--compact" : ""}`}
                onClick={onToggleDrawerWidth}
                aria-pressed={visitorDrawerCompact}
                aria-label={
                  visitorDrawerCompact ? "Expand visitor drawer and profile" : "Compact visitor drawer, hide profile"
                }
              >
                {visitorDrawerCompact ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
            </Tooltip>
          ) : null}
        </div>

        <div
          className={`chatVisitorDrawer__columns${visitorDrawerCompact ? " chatVisitorDrawer__columns--profileHidden" : ""}`}
        >
          <div className="mainDragViewclose">
            <div className="showMainDrag">
              {size == 1 ? (
                <div className="chatVisitorDrawer__chatColumn">
                  <DrageCurrentChat
                    isAnimation={isAnimation}
                    setisAnimation={setisAnimation}
                    messages={messages}
                    setMessages={setMessages}
                    currentActiveVisitorData={currentVisitor}
                    runAi={runAi}
                    setRunAi={setRunAi}
                  />
                </div>
              ) : (
                <div className="chatVisitorDrawer__chatColumn chatVisitorDrawer__chatColumn--past">
                  <DragPastChat currentActiveVisitorData={currentVisitor} />
                </div>
              )}
            </div>
          </div>

          {!visitorDrawerCompact ? (
            <div className="showSideBarDrag">
              <SideBarDrawer currentActiveVisitorData={currentVisitor} />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
